import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { hideModal } from '../store/slices/uiSlice';
import { FiX } from 'react-icons/fi';

const ModalContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { modal } = useSelector((state: RootState) => state.ui);

  // Close modal on ESC key press
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modal) {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [modal]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  // Handle cancel action
  const handleCancel = () => {
    if (modal?.onCancel) {
      modal.onCancel();
    }
    dispatch(hideModal());
  };

  // Handle confirm action
  const handleConfirm = () => {
    if (modal?.onConfirm) {
      modal.onConfirm();
    }
    dispatch(hideModal());
  };

  if (!modal) {
    return null;
  }

  // Determine modal size class
  const sizeClass = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }[modal.size || 'md'];

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center"
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full ${sizeClass} mx-4`}
      >
        <div className="flex items-start justify-between p-4 border-b dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {modal.title}
          </h3>
          <button
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
            onClick={handleCancel}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {typeof modal.content === 'string' ? (
            <p className="text-gray-700 dark:text-gray-300">{modal.content}</p>
          ) : (
            modal.content
          )}
        </div>

        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 flex justify-end space-x-2 rounded-b-lg">
          {!modal.hideCancel && (
            <button
              type="button"
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              onClick={handleCancel}
            >
              {modal.cancelLabel || 'Cancel'}
            </button>
          )}
          <button
            type="button"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            onClick={handleConfirm}
          >
            {modal.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalContainer;